/**
 * Unified Validation Pipeline — Backward-compatible adapter over the governance pipeline.
 *
 * ARCHITECTURE v4.0
 *
 * This file is a thin adapter that maps PipelineResult → UnifiedValidationResult
 * for existing UI consumers. New code should use runPipeline() / runPipelineBatch()
 * from governance/pipeline/orchestrator.ts directly.
 */

import { Question } from '../types'
import { runPipeline, runPipelineBatch } from './pipeline/orchestrator'
import type { ValidationIssue, PipelineResult } from './pipeline/issueContract'
import { assessExplanationDepth, assessDistractorDepth } from './didacticDepth'
import { SeverityLevel, SEVERITY_WEIGHTS } from './utils'

export type { SeverityLevel }

export interface UnifiedIssue {
  domain:
    | 'schema'
    | 'taxonomy'
    | 'governance'
    | 'quality'
    | 'didactic'
    | 'visual'
    | 'answer-leakage'
  severity: SeverityLevel
  code: string
  message: string
  field?: string
  suggestion?: string
}

export interface UnifiedValidationResult {
  questionId: string
  familyId: string
  isCompliant: boolean
  score: number // 0-100
  issues: UnifiedIssue[]
  dimensions: {
    schema: number
    taxonomy: number
    governance: number
    /** Normalised 0–100 (was raw 0–55 sum) */
    didacticDepth: number
    visual: number
    answerLeakage: number
    /** Tier alignment fit score 0-100 */
    tierFit: number
  }
  /** Cognitive level detected */
  cognitiveLevel?: string
  /** Whether question meets minimum per-dimension thresholds */
  meetsMinimumThresholds: boolean
}

export interface GovernanceAuditReport {
  generatedAt: string
  totalQuestions: number
  compliantQuestions: number
  nonCompliantQuestions: number
  issuesBySeverity: Record<SeverityLevel, number>
  avgScore: number
  questionResults: UnifiedValidationResult[]
}

// ─── Pipeline → Unified Adapters ──────────────────────────────────────────────

/**
 * Map pipeline ValidationIssue kind → legacy SeverityLevel.
 */
function mapKindToSeverity(issue: ValidationIssue): SeverityLevel {
  switch (issue.kind) {
    case 'blocker':
      return 'critical'
    case 'coach':
      return 'warning'
    case 'info':
      return 'info'
    default:
      return 'info'
  }
}

/**
 * Map pipeline IssueDomain → legacy UnifiedIssue domain.
 */
function mapDomain(issue: ValidationIssue): UnifiedIssue['domain'] {
  switch (issue.domain) {
    case 'schema':
    case 'type':
      return 'schema'
    case 'graph':
      return 'taxonomy'
    case 'content':
      // Distinguish governance vs didactic based on issue code
      if (
        issue.code.startsWith('DIDACTIC_') ||
        issue.code.includes('EXPLANATION_DEPTH') ||
        issue.code.includes('DISTRACTOR_DEPTH')
      ) {
        return 'didactic'
      }
      return 'governance'
    case 'visual':
      // Distinguish visual vs answer-leakage based on issue code
      if (
        issue.code.includes('ANSWER_LEAK') ||
        issue.code.includes('LEAKAGE')
      ) {
        return 'answer-leakage'
      }
      return 'visual'
    case 'support':
      return 'quality'
    case 'scoring':
    case 'gate':
      return 'governance'
    default:
      return 'governance'
  }
}

/**
 * Convert a pipeline ValidationIssue to a legacy UnifiedIssue.
 */
function toUnifiedIssue(issue: ValidationIssue): UnifiedIssue {
  return {
    domain: mapDomain(issue),
    severity: mapKindToSeverity(issue),
    code: issue.code,
    message: issue.message,
    field: issue.field,
    suggestion: issue.suggestion,
  }
}

/**
 * Compute answer-leakage dimension score from pipeline issues.
 */
function computeAnswerLeakageScore(issues: UnifiedIssue[]): number {
  const leakageIssues = issues.filter((i) => i.domain === 'answer-leakage')
  const penalty = leakageIssues.reduce(
    (total, issue) => total + SEVERITY_WEIGHTS[issue.severity],
    0,
  )
  return Math.max(0, 100 - penalty)
}

/**
 * Compute tier-fit dimension score from graph-domain pipeline issues.
 */
function computeTierFitScore(pipelineResult: PipelineResult): number {
  const graphIssues = (pipelineResult.stageResults[2] ?? []).filter(
    (i) => i.code.includes('TIER') || i.code.includes('PREREQUISITE'),
  )
  if (graphIssues.length === 0) return 100
  const penalty = graphIssues.reduce((sum, issue) => {
    switch (issue.kind) {
      case 'blocker':
        return sum + 30
      case 'coach':
        return sum + Math.round(issue.severity / 4)
      case 'info':
        return sum + 2
      default:
        return sum
    }
  }, 0)
  return Math.max(0, 100 - penalty)
}

/**
 * Adapt a PipelineResult to the legacy UnifiedValidationResult shape.
 */
function adaptPipelineResult(
  question: Question,
  pipelineResult: PipelineResult,
): UnifiedValidationResult {
  // Map all pipeline issues to unified format
  const unifiedIssues = pipelineResult.gate.issues.map(toUnifiedIssue)

  // Didactic depth: still computed separately (pipeline doesn't replicate granular scoring)
  const explanationDepth = assessExplanationDepth(question)
  const distractorDepth = assessDistractorDepth(question)
  const rawDidactic = explanationDepth.score + distractorDepth.score // max 55

  // Add didactic issues to unified issues (pipeline content stage may not catch all)
  explanationDepth.issues.forEach((issue) => {
    const exists = unifiedIssues.some(
      (u) => u.domain === 'didactic' && u.message === issue.message,
    )
    if (!exists) {
      unifiedIssues.push({
        domain: 'didactic',
        severity: issue.severity,
        code: 'DIDACTIC_EXPLANATION',
        message: issue.message,
      })
    }
  })
  distractorDepth.issues.forEach((issue) => {
    const exists = unifiedIssues.some(
      (u) => u.domain === 'didactic' && u.message === issue.message,
    )
    if (!exists) {
      unifiedIssues.push({
        domain: 'didactic',
        severity: issue.severity,
        code: 'DIDACTIC_DISTRACTOR',
        message: issue.message,
      })
    }
  })

  // Compute dimension scores
  const dimensions = {
    schema: Math.min(
      pipelineResult.domainScores.schema ?? 100,
      pipelineResult.domainScores.type ?? 100,
    ),
    taxonomy: pipelineResult.domainScores.graph ?? 100,
    governance: pipelineResult.domainScores.content ?? 100,
    didacticDepth: Math.round((rawDidactic / 55) * 100),
    visual: pipelineResult.domainScores.visual ?? 100,
    answerLeakage: computeAnswerLeakageScore(unifiedIssues),
    tierFit: computeTierFitScore(pipelineResult),
  }

  // Check minimum thresholds per dimension
  const MINIMUM_THRESHOLDS = {
    schema: 70,
    taxonomy: 70,
    governance: 40,
    didacticDepth: 30,
    visual: 50,
    answerLeakage: 70,
    tierFit: 40,
  }

  const meetsMinimumThresholds = Object.entries(MINIMUM_THRESHOLDS).every(
    ([key, min]) => dimensions[key as keyof typeof dimensions] >= min,
  )

  return {
    questionId: pipelineResult.questionId,
    familyId: pipelineResult.familyId,
    isCompliant:
      pipelineResult.gate.verdict !== 'FAIL' && meetsMinimumThresholds,
    score: pipelineResult.gate.score,
    issues: unifiedIssues,
    dimensions,
    cognitiveLevel: explanationDepth.cognitiveLevel,
    meetsMinimumThresholds,
  }
}

// ─── Public API (backward-compatible) ─────────────────────────────────────────

/**
 * Single validation entry point.
 * Now delegates to the governance pipeline and adapts the result.
 *
 * @deprecated Use `runPipeline()` from `governance/pipeline/orchestrator.ts` directly.
 */
export function validateQuestion(question: Question): UnifiedValidationResult {
  const pipelineResult = runPipeline(question)
  return adaptPipelineResult(question, pipelineResult)
}

/**
 * Batch audit — delegates to pipeline batch runner.
 *
 * @deprecated Use `runPipelineBatch()` from `governance/pipeline/orchestrator.ts` directly.
 */
export function runGovernanceAudit(
  questions: Question[],
): GovernanceAuditReport {
  const questionResults = questions.map(validateQuestion)

  const issuesBySeverity: Record<SeverityLevel, number> = {
    critical: 0,
    warning: 0,
    info: 0,
  }

  questionResults.forEach((r) => {
    r.issues.forEach((i) => {
      issuesBySeverity[i.severity]++
    })
  })

  const compliantQuestions = questionResults.filter((r) => r.isCompliant).length
  const avgScore =
    questionResults.length > 0
      ? Math.round(
          questionResults.reduce((sum, r) => sum + r.score, 0) /
            questionResults.length,
        )
      : 100

  return {
    generatedAt: new Date().toISOString(),
    totalQuestions: questionResults.length,
    compliantQuestions,
    nonCompliantQuestions: questionResults.length - compliantQuestions,
    issuesBySeverity,
    avgScore,
    questionResults,
  }
}
